import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authApi, type InvitationInfo } from "../api/auth";
import type { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Select } from "../components/Select";
import { displayCompanyName } from "../utils/companyName";

export default function AcceptInvitePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = searchParams.get("token") ?? "";

  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [locale, setLocale] = useState(navigator.language?.split("-")[0] || "en");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLoadError({ status: 400, code: "MISSING_TOKEN", message: "No token in URL" });
      return;
    }
    authApi
      .inspectInvitation(token)
      .then((r) => setInfo(r))
      .catch((e: ApiError) => setLoadError(e));
  }, [token]);

  if (loadError) {
    return (
      <CenteredCard>
        <h1 style={titleStyle}>{t("invite.unavailable", "Invitation unavailable")}</h1>
        <p>
          {loadError.code === "INVITATION_EXPIRED"
            ? "This invitation has expired. Please contact whoever invited you for a new link."
            : loadError.code === "INVITATION_USED"
              ? "This invitation has already been accepted. Try signing in instead."
              : "We couldn't open this invitation. Please check the link."}
        </p>
        <Link to="/login" style={linkStyle}>
          Go to sign in
        </Link>
      </CenteredCard>
    );
  }

  if (!info) {
    return (
      <CenteredCard>
        <p>{t("invite.loading", "Loading invitation…")}</p>
      </CenteredCard>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    if (password !== confirm) {
      setSubmitError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await authApi.acceptInvitation(token, {
        firstName,
        lastName,
        password,
        locale,
        phone: phone.trim() || undefined,
      });
      await refresh();
      // Land on the profile with the welcome flag so the user is prompted to
      // upload an avatar before heading into the app proper.
      navigate("/profile?welcome=1", { replace: true });
    } catch (err) {
      const apiErr = err as ApiError;
      setSubmitError(translatePasswordError(apiErr));
    } finally {
      setSubmitting(false);
    }
  }

  const passwordRules = checkPasswordClient(password);

  return (
    <CenteredCard>
      <h1 style={titleStyle}>Welcome to {info.companyName ? displayCompanyName(info.companyName) : "IMP Pumps Portal"}</h1>
      <p style={{ color: "#6b7280", fontSize: 14 }}>
        You&apos;re joining as <strong>{info.email}</strong>
        {info.roles.length > 0 ? <> — role: {info.roles.join(", ")}</> : null}.
      </p>

      {submitError && <div style={errorStyle}>{submitError}</div>}

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 12 }}>
          <label style={{ ...labelStyle, flex: 1 }}>
            <span>{t("invite.field.firstName", "First name")}</span>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required style={inputStyle} />
          </label>
          <label style={{ ...labelStyle, flex: 1 }}>
            <span>{t("invite.field.lastName", "Last name")}</span>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} required style={inputStyle} />
          </label>
        </div>

        <label style={labelStyle}>
          <span>{t("invite.field.phone", "Phone")} <span style={{ color: "#9ca3af", fontWeight: 400 }}>({t("common.optional", "optional")})</span></span>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+386 1 234 5678"
            autoComplete="tel"
            maxLength={40}
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>{t("invite.field.password", "Password")}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            style={inputStyle}
          />
          <ul style={hintListStyle}>
            <Rule ok={passwordRules.minLength}>{t("invite.pw.length", "At least 12 characters")}</Rule>
            <Rule ok={passwordRules.hasUpper}>{t("invite.pw.upper", "One uppercase letter (A–Z)")}</Rule>
            <Rule ok={passwordRules.hasLower}>{t("invite.pw.lower", "One lowercase letter (a–z)")}</Rule>
            <Rule ok={passwordRules.hasDigit}>{t("invite.pw.digit", "One digit (0–9)")}</Rule>
            <li style={{ color: "#6b7280" }}>
              Cannot be a commonly leaked password (we check against a public breach list).
            </li>
          </ul>
        </label>
        <label style={labelStyle}>
          <span>{t("invite.field.confirm", "Confirm password")}</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            required
            style={inputStyle}
          />
        </label>

        <label style={labelStyle}>
          <span>{t("invite.field.language", "Language")}</span>
          <Select
            value={locale}
            onChange={setLocale}
            ariaLabel={t("acceptInvite.field.locale", "Language")}
            options={[
              { value: "en", label: "English" },
              { value: "sl", label: "Slovenščina" },
              { value: "de", label: "Deutsch" },
              { value: "hr", label: "Hrvatski" },
              { value: "sr", label: "Srpski" },
              { value: "it", label: "Italiano" },
              { value: "fr", label: "Français" },
              { value: "ru", label: "Русский" },
            ]}
          />
        </label>

        <button type="submit" disabled={submitting} style={btnStyle}>
          {submitting ? "Creating account…" : "Create account"}
        </button>
      </form>
    </CenteredCard>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return <div style={shellStyle}><div style={cardStyle}>{children}</div></div>;
}

function Rule({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li style={{ color: ok ? "#15803d" : "#6b7280", listStyle: "none", display: "flex", gap: 6 }}>
      <span aria-hidden>{ok ? "✓" : "○"}</span>
      <span>{children}</span>
    </li>
  );
}

function checkPasswordClient(p: string) {
  return {
    minLength: p.length >= 12,
    hasUpper: /[A-Z]/.test(p),
    hasLower: /[a-z]/.test(p),
    hasDigit: /\d/.test(p),
  };
}

function translatePasswordError(err: ApiError): string {
  switch (err.code) {
    case "PASSWORD_TOO_SHORT":
      return "Password must be at least 12 characters.";
    case "PASSWORD_NEEDS_UPPERCASE":
      return "Password must contain at least one uppercase letter (A–Z).";
    case "PASSWORD_NEEDS_LOWERCASE":
      return "Password must contain at least one lowercase letter (a–z).";
    case "PASSWORD_NEEDS_DIGIT":
      return "Password must contain at least one digit (0–9).";
    case "PASSWORD_BREACHED":
      return "This password appears in a public breach corpus. Pick a different one.";
    case "INVITATION_EXPIRED":
      return "This invitation has expired. Ask the inviter to send a new one.";
    case "INVITATION_INVALID":
      return "Invitation is invalid. The link may have been used or revoked.";
    default:
      return err.message || "Could not accept the invitation.";
  }
}

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f4f6f8",
  padding: 16,
};
const cardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 480,
  background: "#fff",
  padding: 32,
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,.08)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
const titleStyle: React.CSSProperties = { margin: 0, color: "#0b3a6f", fontSize: 24 };
const labelStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, fontSize: 14 };
const inputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
  fontSize: 15,
};
const btnStyle: React.CSSProperties = {
  padding: "12px 16px",
  background: "#0b3a6f",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
const linkStyle: React.CSSProperties = { color: "#0b3a6f", fontSize: 14 };
const errorStyle: React.CSSProperties = {
  padding: 12,
  background: "#fef2f2",
  color: "#b91c1c",
  borderRadius: 6,
  fontSize: 14,
};
const hintListStyle: React.CSSProperties = {
  margin: "8px 0 0",
  padding: 0,
  fontSize: 12,
  display: "flex",
  flexDirection: "column",
  gap: 3,
};
